import React from 'react';
import { APP_COLORS as C, APP_FONT as F } from '../tokens';
import type { VelaQueueItem, VelaQueueKind } from '../types';

type Props = {
  items: ReadonlyArray<VelaQueueItem>;
  totalCount?: number;
  onReviewItem?: (item: VelaQueueItem) => void;
  onReviewAll?: () => void;
};

function QueueIcon({ kind }: { kind: VelaQueueKind }) {
  const stroke = kind === 'payroll' ? C.tealInk
    : kind === 'invoice' ? C.amber
    : kind === 'cert' ? C.red
    : kind === 'categorize' ? C.vela
    : C.vela;
  const common = {
    width: 22, height: 22, fill: 'none', stroke, strokeWidth: 2.2,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  switch (kind) {
    case 'payroll':
      return <svg viewBox="0 0 24 24" {...common}><rect x="3" y="6" width="18" height="13" rx="2"/><path d="M3 10h14a2 2 0 012 2v2a2 2 0 01-2 2H3"/><circle cx="16" cy="13" r="1" fill={stroke} stroke="none"/></svg>;
    case 'invoice':
      return <svg viewBox="0 0 24 24" {...common}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="12" x2="12" y2="15"/><line x1="12" y1="18" x2="12" y2="18.5"/></svg>;
    case 'categorize':
      return <svg viewBox="0 0 24 24" {...common}><path d="M12 2L2 12l10 10 10-10V4a2 2 0 00-2-2z"/><circle cx="16" cy="8" r="1.5"/></svg>;
    case 'cert':
      return <svg viewBox="0 0 24 24" {...common}><path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z"/><polyline points="9 12 11 14 15 10"/></svg>;
    default:
      return <svg viewBox="0 0 24 24" {...common}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
  }
}

export default function VelaReviewQueue({
  items,
  totalCount,
  onReviewItem,
  onReviewAll,
}: Props) {
  const count = totalCount ?? items.length;
  const [hoveredCta, setHoveredCta] = React.useState<string | null>(null);

  return (
    <div style={{
      background: C.card,
      border: '1px solid ' + C.line,
      borderRadius: 14,
      padding: '20px 22px',
      fontFamily: F.family,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          Vela prepared, ready for your review
          <span style={{
            background: C.velaSoft, color: C.vela, fontSize: 11, fontWeight: 700,
            padding: '2px 8px', borderRadius: 999, letterSpacing: '0.03em',
          }}>{count}</span>
        </h3>
        <span onClick={onReviewAll} style={{ fontSize: 12, color: C.tealInk, fontWeight: 700, cursor: 'pointer' }}>
          Review all
        </span>
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: C.sub, padding: '18px 0', fontStyle: 'italic' }}>
          Nothing in the queue right now. Vela will add items here as it drafts them.
        </div>
      ) : (
        items.map((item, i) => (
          <div key={item.id} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '11px 0',
            borderBottom: i === items.length - 1 ? '0' : '1px solid ' + C.lineSoft,
            fontSize: 13,
          }}>
            <div style={{ width: 24, height: 24, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <QueueIcon kind={item.kind} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: C.ink }}>{item.title}</div>
              <div style={{ fontSize: 11.5, color: C.sub, marginTop: 2 }}>{item.sub}</div>
            </div>
            <button
              onMouseEnter={() => setHoveredCta(item.id)}
              onMouseLeave={() => setHoveredCta(null)}
              onClick={() => onReviewItem?.(item)}
              style={{
                background: hoveredCta === item.id ? C.teal : C.ink,
                color: '#fff', border: 0, borderRadius: 7,
                padding: '6px 11px', fontWeight: 600, fontSize: 11.5,
                cursor: 'pointer', fontFamily: F.family,
              }}
            >
              {item.ctaLabel}
            </button>
          </div>
        ))
      )}
    </div>
  );
}
